import type { MessageData, PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { replaceParams } from "@/app/api/endpoints";
import type {
	CreateNewOrganizationUserRequest,
	CreateOrganizationUserRequest,
	OrganizationUser,
	UpdateOrganizationUserRequest,
} from "../types";

// ─── Endpoints ────────────────────────────────────────────────────────
const organizationUsersEndpoints = {
	list: "/api/organization/:organizationId/users",
	getById: "/api/organization/:organizationId/users/:id",
	create: "/api/organization/:organizationId/users",
	update: "/api/organization/:organizationId/users/:id",
	delete: "/api/organization/:organizationId/users/:id",
} as const;

// ─── Service ──────────────────────────────────────────────────────────
export const organizationUsersService = {
	async list(
		organizationId: string,
		params?: { page?: number; limit?: number }
	): Promise<PaginatedResponse<OrganizationUser>> {
		const url = replaceParams(organizationUsersEndpoints.list, { organizationId });
		const response = await apiClient.get<PaginatedResponse<OrganizationUser>>(url, { params });
		return response.data;
	},

	async getById(organizationId: string, id: string): Promise<OrganizationUser> {
		const url = replaceParams(organizationUsersEndpoints.getById, { organizationId, id });
		const response = await apiClient.get<SuccessResponse<OrganizationUser>>(url);
		return response.data.data;
	},

	async create(
		organizationId: string,
		data:
			| CreateOrganizationUserRequest
			| CreateNewOrganizationUserRequest
			| (CreateOrganizationUserRequest | CreateNewOrganizationUserRequest)[]
	): Promise<OrganizationUser | OrganizationUser[]> {
		const url = replaceParams(organizationUsersEndpoints.create, { organizationId });
		const body = Array.isArray(data) ? data : [data];
		const response = await apiClient.post<SuccessResponse<OrganizationUser | OrganizationUser[]>>(
			url,
			body
		);
		return response.data.data;
	},

	async update(
		organizationId: string,
		id: string,
		data: UpdateOrganizationUserRequest
	): Promise<OrganizationUser> {
		const url = replaceParams(organizationUsersEndpoints.update, { organizationId, id });
		const response = await apiClient.patch<SuccessResponse<OrganizationUser>>(url, data);
		return response.data.data;
	},

	async delete(organizationId: string, id: string): Promise<void> {
		const url = replaceParams(organizationUsersEndpoints.delete, { organizationId, id });
		await apiClient.delete<SuccessResponse<MessageData>>(url);
	},
};
